import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const AchievementsSection = () => {
  const achievements = [
    {
      icon: "Users",
      number: "500+",
      title: "Trusted Surgeons",
      description: "Medical professionals across Malaysia rely on our expertise and quality products for their surgical procedures.",
      highlight: "Leading gastroenterologists and endoscopists"
    },
    {
      icon: "TrendingUp",
      number: "35%",
      title: "Market Share",
      description: "Commanding position in Malaysia's endoscopic equipment market through consistent quality and service excellence.",
      highlight: "Fastest growing medical distributor"
    },
    {
      icon: "Award",
      number: "12+",
      title: "Years Excellence",
      description: "Over a decade of continuous innovation and service in medical technology distribution since 2012.",
      highlight: "Consistent year-over-year growth"
    },
    {
      icon: "Building2",
      number: "200+",
      title: "Healthcare Facilities",
      description: "Hospitals, clinics, and medical centers nationwide trust our products and technical support services.",
      highlight: "From private clinics to major hospitals"
    }
  ];

  const milestones = [
    {
      year: "2012",
      title: "Company Founded",
      description: "Established Endosurgery Sdn Bhd with focus on endoscopic equipment distribution"
    },
    {
      year: "2015",
      title: "GDPMD Certification",
      description: "Achieved Good Distribution Practice for Medical Devices certification"
    },
    {
      year: "2018",
      title: "Market Leadership",
      description: "Became Malaysia's leading distributor of endoscopic surgical instruments"
    },
    {
      year: "2021",
      title: "Digital Transformation",
      description: "Launched comprehensive digital platform for product catalog and support"
    },
    {
      year: "2024",
      title: "Innovation Hub",
      description: "Established training center for medical professionals and continuous education"
    }
  ];

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Our Achievements
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Celebrating milestones that reflect our commitment to advancing medical care in Malaysia 
            through innovative technology and exceptional service.
          </p>
        </motion.div>

        {/* Key Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {achievements?.map((achievement, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 border border-gray-200 dark:border-gray-600 hover:shadow-medical-hover transition-all duration-300"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon 
                    name={achievement?.icon} 
                    size={28} 
                    color="var(--color-primary)"
                    className="group-hover:text-white transition-colors duration-300"
                  />
                </div>
                <div className="text-4xl font-bold text-primary mb-2">{achievement?.number}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{achievement?.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-3">{achievement?.description}</p>
                <div className="text-xs font-medium text-accent bg-accent/10 px-3 py-1 rounded-full inline-block">
                  {achievement?.highlight}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-slate-50 via-white to-cyan-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-2xl p-8 md:p-12"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Our Journey</h3>
            <p className="text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Key milestones that have shaped our growth and established our leadership in medical technology distribution.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-primary via-accent to-success h-full hidden lg:block"></div>

            <div className="space-y-12 lg:space-y-16">
              {milestones?.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex-col lg:gap-12 gap-6`}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center`}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-medical border border-gray-200 dark:border-gray-600">
                      <div className="text-2xl font-bold text-primary mb-2">{milestone?.year}</div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{milestone?.title}</h4>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{milestone?.description}</p>
                    </div>
                  </div>

                  {/* Timeline Node */}
                  <div className="relative z-10 w-4 h-4 bg-primary rounded-full border-4 border-white dark:border-gray-800 shadow-lg hidden lg:block"></div>

                  <div className="lg:w-1/2 hidden lg:block"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Recognition Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-medical border border-gray-200 dark:border-gray-600">
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} color="var(--color-success)" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">GDPMD Certified</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">Quality Assurance</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} color="var(--color-primary)" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">ISO 13485</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">International Standard</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Award" size={24} color="var(--color-accent)" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">Industry Leader</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">Market Recognition</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AchievementsSection;